// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#cost-management
import * as React from 'react';
import { AlertTriangle, KeyRound } from 'lucide-react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { CursorUsageSettings, SetCursorAdminKey } from '@greenlight/shared';

export interface CursorAdminKeyRowProps {
  cursorUsage: CursorUsageSettings;
  /** Stores or replaces the key. Write-only; rejects when the server refused it. */
  onSetKey: (input: SetCursorAdminKey) => Promise<void> | void;
  onRemoveKey: () => Promise<void> | void;
}

const FAILURE_COPY: Record<NonNullable<CursorUsageSettings['failure']>['code'], string> = {
  key_rejected:
    'Cursor no longer accepts this key. It was probably revoked, or its owner left the team.',
  unreachable: "Cursor's Admin API could not be read on the last attempt.",
};

const absolute = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/**
 * The Cursor Admin API key inside the Build token usage card. Cursor sessions
 * send no counts of their own, so without a key they are not counted.
 */
export function CursorAdminKeyRow({
  cursorUsage,
  onSetKey,
  onRemoveKey,
}: CursorAdminKeyRowProps): React.JSX.Element {
  const keyId = React.useId();
  const [editing, setEditing] = React.useState(false);
  const [key, setKey] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);
  const [removeError, setRemoveError] = React.useState<string | null>(null);

  const configured = cursorUsage.admin_key_configured;
  const failure = cursorUsage.failure;

  function close(): void {
    setEditing(false);
    setKey('');
    setError(null);
  }

  async function handleSave(): Promise<void> {
    const trimmed = key.trim();
    if (trimmed.length < 8 || /\s/.test(trimmed)) {
      setError('Paste the whole key Cursor showed you, with no spaces.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSetKey({ api_key: trimmed });
      close();
    } catch {
      setError('The key was not saved. Check that it is a team Admin API key and try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(): Promise<void> {
    setRemoving(true);
    setRemoveError(null);
    try {
      await onRemoveKey();
      setConfirmingRemove(false);
    } catch {
      setRemoveError('Could not remove the key. It is still stored. Try again.');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="space-y-3 border-t border-border px-5 py-3.5" data-testid="cursor-admin-key">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground">
              {configured ? 'Cursor Admin API key stored' : 'Cursor sessions are not counted'}
            </span>
            <span className="text-xs text-muted-foreground">
              {configured
                ? cursorUsage.synced_at
                  ? `Cursor usage last read ${absolute(cursorUsage.synced_at)}.`
                  : 'Cursor usage is read once an hour.'
                : "Cursor doesn't keep token counts on the builder's machine. Add a team Admin API key and Greenlight reads them from Cursor each hour, for sessions the plugin tied to an app."}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 pl-3">
          <Button
            variant="outline"
            size="sm"
            className="h-11 md:h-9"
            aria-label={configured ? 'Replace Cursor Admin API key' : 'Add Cursor Admin API key'}
            onClick={() => (editing ? close() : setEditing(true))}
          >
            {configured ? 'Replace' : 'Add'}
          </Button>
          {configured && (
            <Button
              variant="outline"
              size="sm"
              className="h-11 md:h-9"
              aria-label="Remove Cursor Admin API key"
              onClick={() => {
                setRemoveError(null);
                setConfirmingRemove(true);
              }}
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      {configured && failure && (
        <Alert variant="warning" data-testid="cursor-usage-failure">
          <AlertTriangle aria-hidden="true" />
          <AlertTitle>Cursor usage is not being read</AlertTitle>
          <AlertDescription>
            <p>
              {FAILURE_COPY[failure.code]} Last attempt {absolute(failure.at)}.{' '}
              {failure.code === 'key_rejected'
                ? 'Replace the key to start again; sessions since then are picked up for two days after they end.'
                : 'Greenlight tries again next hour.'}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {editing && (
        <div className="space-y-2 rounded-gl border border-border p-3">
          <label htmlFor={keyId} className="block text-xs font-medium text-foreground">
            Cursor team Admin API key
          </label>
          <Input
            id={keyId}
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="h-11 md:h-9 font-mono"
          />
          <p className="text-xs text-muted-foreground">
            A Cursor team admin creates one under Cursor&apos;s team settings, Advanced, Admin API
            keys. Greenlight checks it with Cursor, stores it in the vault, and never shows it
            again. It reads your whole team&apos;s usage, but only sessions tied to an app here are
            counted.
          </p>
          {error !== null && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-11 md:h-9"
              aria-label="Save Cursor Admin API key"
              disabled={saving}
              onClick={() => void handleSave()}
            >
              {saving ? 'Checking…' : 'Save'}
            </Button>
            <Button variant="outline" size="sm" className="h-11 md:h-9" onClick={close}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <GLDrawer
        variant="confirm"
        mobileBreakpoint={768}
        open={confirmingRemove}
        onOpenChange={(open) => {
          if (!open) setConfirmingRemove(false);
        }}
        preventDismiss={removing}
        title="Remove the Cursor Admin API key?"
        description="Cursor sessions stop being counted"
        contentProps={{ 'data-testid': 'remove-cursor-key-dialog' }}
        footer={
          <>
            <Button
              variant="outline"
              className="h-11 md:h-9"
              disabled={removing}
              onClick={() => setConfirmingRemove(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="h-11 md:h-9"
              disabled={removing}
              onClick={() => void handleRemove()}
              data-testid="confirm-remove-cursor-key"
            >
              {removing ? 'Removing…' : 'Remove key'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm leading-relaxed">
          <p>
            Greenlight deletes the stored key and stops reading Cursor usage. Counts already read
            stay on the Cost page. Revoke the key in Cursor too if nothing else uses it.
          </p>
          {removeError !== null && (
            <p role="alert" className="text-destructive">
              {removeError}
            </p>
          )}
        </div>
      </GLDrawer>
    </div>
  );
}
